import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Loader2, CheckCircle2, FileText, X } from "lucide-react";
import { trackChecklistForm, trackDownload } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

export const LeadForm = ({ material, testid = "lead" }) => {
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", municipio: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "mt-2 w-full border-b border-[#1A2218]/20 bg-transparent py-2 text-base text-[#1A2218] outline-none transition-colors duration-300 focus:border-[#5D995E]";

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Envio indisponível no momento.");
    if (!form.consent) return toast.error("É preciso aceitar a Política de Privacidade.");
    setLoading(true);
    try {
      await axios.post(`${API}/lead`, { ...form, material: material.title, website: e.currentTarget.website?.value || "" });
      trackChecklistForm();
      setDone(true);
      toast.success("Pronto! Seu material está liberado abaixo.");
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente ou fale pelo WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid={`${testid}-form`} onSubmit={submit} className="space-y-5">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

      <input required minLength={2} data-testid={`${testid}-name`} placeholder="Seu nome" value={form.name} onChange={set("name")} className={inputCls} />
      <input required minLength={8} data-testid={`${testid}-whatsapp`} placeholder="Seu WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
      <input type="email" data-testid={`${testid}-email`} placeholder="Seu e-mail (opcional)" value={form.email} onChange={set("email")} className={inputCls} />
      <input data-testid={`${testid}-municipio`} placeholder="Seu município" value={form.municipio} onChange={set("municipio")} className={inputCls} />
      <label className="flex items-start gap-3 text-sm text-[#4A5549]">
        <input
          type="checkbox"
          required
          checked={form.consent}
          onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
          data-testid={`${testid}-consent`}
          className="mt-0.5 h-4 w-4 accent-[#233D25]"
        />
        <span>
          Autorizo o contato da Melo Agro Engenharia e li a{" "}
          <Link to="/politica-de-privacidade" className="font-semibold text-[#233D25] underline underline-offset-2">Política de Privacidade</Link>. *
        </span>
      </label>
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testid}-submit`}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#233D25] px-6 py-4 font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#3E5B3F] disabled:opacity-70"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
        {loading ? "Enviando..." : "Receber o material"}
      </button>
      {done && (
        <div data-testid={`${testid}-success`} className="rounded-2xl bg-[#F2EFE9] p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#233D25]">
            <CheckCircle2 className="h-4 w-4" /> Recebemos seus dados!
          </p>
          <a
            data-testid={`${testid}-download`}
            href={material.file}
            download
            onClick={() => trackDownload(material.title)}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#5D995E] px-6 py-3.5 font-semibold text-white transition-colors duration-300 hover:bg-[#233D25]"
          >
            <Download className="h-5 w-5" /> Baixar o PDF
          </a>
          <p className="mt-2 text-xs text-[#4A5549]">Retornamos em até 1 dia útil pelo WhatsApp.</p>
        </div>
      )}
    </form>
  );
};

const MATERIAL = { title: "Checklist de documentos para o crédito rural do Plano Safra", file: "/materiais/checklist-credito-rural-plano-safra.pdf" };

// Faixa compacta de uma linha + modal com o formulário (a home mantém 1 formulário visível: o de contato).
export const LeadSection = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <section id="materiais" data-testid="lead-section" className="bg-[#F2EFE9] py-4 sm:py-6">
      <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-3 px-5 sm:px-8 md:flex-row md:items-center md:justify-between">
        <p className="flex items-center gap-2.5 text-sm text-[#2C332A] sm:text-base">
          <FileText className="h-5 w-5 shrink-0 text-[#233D25]" />
          <span>
            <span className="font-semibold text-[#1A2218]">Material grátis:</span> Checklist de documentos para o crédito rural do Plano Safra (PDF)
          </span>
        </p>
        <button
          type="button"
          data-testid="lead-open-modal"
          onClick={() => setOpen(true)}
          className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-[#233D25] px-6 py-3 text-sm font-semibold text-[#F9F8F3] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#3E5B3F]"
        >
          <Download className="h-4 w-4" /> Baixar checklist
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="lead-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto px-4 py-6"
          >
            <div className="absolute inset-0 bg-[#1B2F1D]/60 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-[28px] border border-[#E5E0D5] bg-white p-6 shadow-[0_40px_120px_-30px_rgba(31,58,36,0.5)] sm:p-8"
            >
              <button
                type="button"
                data-testid="lead-modal-close"
                onClick={() => setOpen(false)}
                aria-label="Fechar"
                className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-[#4A5549] transition-colors duration-300 hover:bg-[#F2EFE9]"
              >
                <X className="h-5 w-5" />
              </button>
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#5D995E]">Material grátis</p>
              <h3 className="mt-2 pr-8 font-serif text-2xl leading-snug text-[#1A2218]">Checklist de documentos para o crédito rural do Plano Safra</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4A5549]">
                A lista completa do que o banco costuma pedir — matrícula, cadastros, orçamentos e mais.
              </p>
              <div className="mt-5 rounded-[20px] bg-[#F2EFE9] p-5">
                <LeadForm material={MATERIAL} testid="lead-home" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
